import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, ShieldCheck, Truck, RotateCcw, MessageCircle, Minus, Plus, ChevronDown } from 'lucide-react';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import ProductGrid from '../components/ProductGrid';
import FrequentlyBoughtTogether from '../components/FrequentlyBoughtTogether';
import ProductDescription from '../components/ProductDescription';
import LavapavimentiDescription from '../components/LavapavimentiDescription';
import ProductReviews from '../components/ProductReviews';
import ProDescription from '../components/ProDescription';
import { toast } from 'sonner';

export default function ProductPage() {
  const { slug } = useParams();
  const { getProduct, products, loading } = useProducts();
  const product = getProduct(slug);
  const { addItem, setIsOpen } = useCart();
  const { guardPurchase } = useSiteSettings();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [openAcc, setOpenAcc] = useState('uso');

  useEffect(() => {
    if (product?.id) {
      import('../analytics/track').then(({ track }) =>
        track('product_view', { product_id: product.id, element_name: product.name, value: product.price, currency: 'EUR' }));
    }
  }, [product?.id]);

  if (loading) return <main className="container-essenya py-32 text-center text-essenya-text">Caricamento…</main>;
  if (!product) return <main className="container-essenya py-32 text-center">Prodotto non trovato. <Link to="/shop" className="underline">Torna allo shop</Link></main>;

  const related = products.filter(p => p.category === product.category && p.id !== product.id && !p.hidden).slice(0, 4);

  const buyNow = () => {
    if (guardPurchase()) return;
    addItem(product.id, qty);
    setIsOpen(false);
    navigate('/checkout');
  };

  return (
    <main data-testid="product-page" className="pt-8 pb-20">
      <div className="container-essenya">
        <Link to="/shop" className="text-sm text-essenya-text hover:underline">← Tutti i prodotti</Link>

        <div className="grid lg:grid-cols-2 gap-12 mt-6">
          <div className="space-y-4">
            <div className="aspect-square bg-essenya-surface2 rounded-2xl overflow-hidden">
              <img src={product.images[active]} alt={product.name} className="w-full h-full object-contain p-8" />
            </div>
            <div className="grid grid-cols-4 gap-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`product-thumb-${i}`} className={`aspect-square rounded-lg overflow-hidden border-2 bg-essenya-surface2 ${i === active ? 'border-essenya-ink' : 'border-transparent'}`}>
                  <img src={img} alt="" className="w-full h-full object-contain p-2" />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:sticky lg:top-32 lg:self-start space-y-5">
            <div>
              <div className="text-eyebrow">{product.categoryLabel} · {product.fragranceLabel}</div>
              <h1 className="font-display text-4xl md:text-5xl mt-2">{product.name}</h1>
              <p className="text-essenya-text text-lg mt-2">{product.subtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} strokeWidth={1.5} className={i < Math.round(product.rating) ? 'fill-essenya-ink text-essenya-ink' : 'text-essenya-border'} />)}
              <span className="text-sm text-essenya-text">{product.rating} · {product.reviews} recensioni</span>
            </div>
            <p className="text-base leading-relaxed">{product.long}</p>
            <div className="flex items-baseline gap-3">
              <span className="font-display text-3xl">{product.price.toFixed(2).replace('.', ',')}€</span>
              <span className="text-essenya-text text-sm">{product.format}</span>
            </div>

            <div className="flex flex-wrap gap-2 text-xs">
              <Tag>Intensità: {product.intensity}</Tag>
              <Tag>Sensazione: {product.sensation}</Tag>
              <Tag>Made in Italy</Tag>
            </div>

            {product.category === 'bundle' ? (
              <Link to="/bundle" data-testid="product-create-bundle" className="btn-primary w-full block text-center mt-2">Crea il tuo bundle</Link>
            ) : (
            <>
            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center border border-essenya-border rounded-lg">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" aria-label="Diminuisci" data-testid="qty-decrease"><Minus size={14} strokeWidth={1.5} /></button>
                <span className="px-3 w-10 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-3" aria-label="Aumenta" data-testid="qty-increase"><Plus size={14} strokeWidth={1.5} /></button>
              </div>
              <button data-testid="product-add-to-cart" onClick={() => { addItem(product.id, qty); toast.success('Aggiunto al carrello'); }} className="btn-primary flex-1">Aggiungi al carrello</button>
            </div>
            <button onClick={buyNow} data-testid="product-buy-now" className="btn-secondary w-full block text-center">Compra ora</button>
            </>
            )}

            <div className="grid grid-cols-2 gap-3 pt-4">
              <Badge I={Truck} t="Spedizione tracciata" />
              <Badge I={ShieldCheck} t="Pagamento sicuro" />
              <Badge I={RotateCcw} t="Reso facile" />
              <Badge I={MessageCircle} t="Assistenza WhatsApp" />
            </div>

            <div className="pt-4 space-y-2 border-t border-essenya-border">
              {[
                { id: 'uso', t: 'Come si usa', c: product.usage },
                { id: 'benefits', t: 'Perché lo amerai', c: <ul className="list-disc pl-5 space-y-1">{product.benefits.map((b, i) => <li key={i}>{b}</li>)}</ul> },
                { id: 'ing', t: 'Ingredienti e avvertenze', c: 'Formula a base di tensioattivi vegetali e fragranza profumata. Tenere fuori dalla portata dei bambini. Evitare il contatto con gli occhi.' },
                { id: 'faq', t: 'FAQ prodotto', c: 'Quanto dura il profumo? Fino a 12 ore dopo l\'uso. Compatibile con tutti i pavimenti lavabili.' },
              ].map(item => (
                <div key={item.id} className="border-b border-essenya-border" data-testid={`accordion-${item.id}`}>
                  <button onClick={() => setOpenAcc(openAcc === item.id ? null : item.id)} className="w-full py-4 flex justify-between items-center text-left font-medium">
                    {item.t}<ChevronDown size={18} strokeWidth={1.5} className={`transition-transform ${openAcc === item.id ? 'rotate-180' : ''}`} />
                  </button>
                  {openAcc === item.id && <div className="pb-4 text-essenya-text text-sm">{item.c}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {product.category === 'lavapavimenti'
          ? <LavapavimentiDescription product={product} />
          : product.category === 'sgrassatori'
            ? <ProductDescription product={product} />
            : product.category === 'professional'
              ? <ProDescription product={product} />
              : null}

        <ProductReviews product={product} />

        <FrequentlyBoughtTogether product={product} />

        {related.length > 0 && (
          <section className="mt-24">
            <h2 className="font-display text-3xl md:text-4xl mb-8">Prodotti consigliati</h2>
            <ProductGrid products={related} />
          </section>
        )}
      </div>

      {/* Sticky mobile add to cart */}
      <div data-testid="sticky-add-to-cart" className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-essenya-border p-3 z-30 flex gap-3 items-center">
        <div>
          <p className="font-display text-lg leading-none">{product.price.toFixed(2).replace('.', ',')}€</p>
          <p className="text-xs text-essenya-text">{product.format}</p>
        </div>
        <button onClick={() => { addItem(product.id, qty); setIsOpen(true); }} className="btn-primary flex-1 py-3" data-testid="sticky-add-button">Aggiungi al carrello</button>
      </div>
    </main>
  );
}

const Tag = ({ children }) => <span className="bg-essenya-surface2 border border-essenya-border rounded-full px-3 py-1">{children}</span>;
const Badge = ({ I, t }) => <div className="flex items-center gap-2 text-sm text-essenya-text"><I size={16} strokeWidth={1.5} />{t}</div>;
